import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFamilyMembers, useHouseholdSettings, useSubscription } from '../hooks/useSupabase';
import { openBillingPortal, isPremium } from '../lib/billing';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { LoadingSpinner, ErrorState } from './ui/StateViews';
import DeleteAccountModal from './DeleteAccountModal';
import Avatar from './Avatar';
import { exportUserData } from '../lib/dataExport';
import { supabase, isOnline } from '../lib/supabase';
import { formatMealTime } from '../lib/mealTimeUtils';
import { isNative } from '../lib/platform';
import { MealIcon } from './MealIcon';
import { useImageUpload } from '../hooks/useImageUpload';

export default function SettingsScreen() {
  const { user, currentMember, membership, householdId, signOut, updatePassword } = useAuth();
  const { members, loading, error, refetch, updateMember } = useFamilyMembers(householdId);
  const { mealTimes, plannedMeals, loading: _settingsLoading, updateMealTimes, updatePlannedMeals } = useHouseholdSettings(householdId);
  const debounceRef = useRef(null);

  const handleMealTimeChange = useCallback((meal, value) => {
    const next = { ...mealTimes, [meal]: value };
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => updateMealTimes(next), 500);
  }, [mealTimes, updateMealTimes]);

  const handleToggleMeal = useCallback((meal) => {
    const next = plannedMeals.includes(meal)
      ? plannedMeals.filter(m => m !== meal)
      : [...plannedMeals, meal];
    if (next.length === 0) return; // keep at least one meal selected
    updatePlannedMeals(next);
  }, [plannedMeals, updatePlannedMeals]);

  const { uploading: photoUploading, uploadMemberAvatar } = useImageUpload();
  const [photoError, setPhotoError] = useState(null);
  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file || !currentMember) return;
    setPhotoError(null);
    const { url, error: upErr } = await uploadMemberAvatar(file, householdId, currentMember.id);
    if (upErr) { setPhotoError(upErr); return; }
    await updateMember(currentMember.id, { avatar_url: url });
    refetch();
  };

  const [editingName, setEditingName] = useState(false);
  const [name, setName] = useState(currentMember?.name || '');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState(null);
  const [pwDone, setPwDone] = useState(false);
  const { subscription } = useSubscription(householdId);
  const premium = isPremium(subscription);
  // Portal only works for real Stripe customers — comped beta households
  // (synthetic comp_ customer ids) have nothing to manage there
  const canManageSubscription =
    currentMember?.type === 'adult' &&
    subscription &&
    !subscription.stripe_customer_id?.startsWith('comp_');
  const showUpgrade = currentMember?.type === 'adult' && !premium;
  const navigate = useNavigate();
  const { supported: pushSupported, permission: pushPermission, isSubscribed: pushSubscribed, subscribe: pushSubscribe, unsubscribe: pushUnsubscribe, loading: pushLoading } = usePushNotifications();

  const handleTogglePush = async () => {
    if (pushSubscribed) {
      await pushUnsubscribe();
    } else {
      await pushSubscribe();
    }
  };

  const handleSaveName = async () => {
    if (!name.trim() || name.trim() === currentMember?.name) {
      setEditingName(false);
      setName(currentMember?.name || '');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await updateMember(currentMember.id, { name: name.trim() });
      setEditingName(false);
    } catch {
      setSaveError('Failed to save name. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const isLastMember = members.filter((m) => !m.archived_at).length <= 1;

  const handleExport = async () => {
    if (!isOnline()) { alert('You must be online to export data.'); return; }
    setExporting(true);
    try {
      await exportUserData(householdId);
    } catch {
      alert('Failed to export data. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handleManageSubscription = async () => {
    if (!isOnline()) { alert('You must be online to manage your subscription.'); return; }
    setOpeningPortal(true);
    const { error } = await openBillingPortal();
    // On success the browser navigates away — only failures reach here
    if (error) {
      alert(error);
      setOpeningPortal(false);
    }
  };

  const handleChangePassword = async () => {
    setPwError(null);
    if (newPassword.length < 8) {
      setPwError('Password must be at least 8 characters.');
      return;
    }
    setPwSaving(true);
    const { error } = await updatePassword(newPassword);
    setPwSaving(false);
    if (error) {
      setPwError(error.message);
      return;
    }
    setNewPassword('');
    setShowPasswordForm(false);
    setPwDone(true);
  };

  const handleDeleteAccount = async () => {
    if (!isOnline()) throw new Error('offline');
    const { data: { session } } = await supabase.auth.getSession();
    const res = await supabase.functions.invoke('delete-account', {
      headers: { Authorization: `Bearer ${session.access_token}` },
    });
    if (res.error) throw res.error;
    const body = res.data;
    if (body?.error) throw new Error(body.error);
    await signOut();
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorState message="Couldn't load settings" onRetry={refetch} />;

  return (
    <div className="space-y-4 lg:space-y-6">
      <h2 className="font-display text-xl lg:text-2xl font-semibold text-ink">Settings</h2>

      {/* Two-column grid on desktop */}
      <div className="lg:grid lg:grid-cols-2 lg:gap-6 space-y-4 lg:space-y-0">
        {/* Profile Card */}
        <div className="bg-panel rounded-2xl p-5 lg:p-6 border border-hairline">
        <div className="flex items-center gap-4">
          <label className="relative shrink-0 cursor-pointer" aria-label="Add or change your profile photo" title="Add or change photo">
            <span className="text-3xl w-12 h-12 flex items-center justify-center bg-surface rounded-full overflow-hidden">
              <Avatar
                url={members.find((m) => m.id === currentMember?.id)?.avatar_url}
                fallback={members.find((m) => m.id === currentMember?.id)?.avatar || '🧑'}
                name={currentMember?.name}
              />
            </span>
            <span className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-primary text-primary-text flex items-center justify-center ring-2 ring-panel">
              {photoUploading ? (
                <svg className="w-3 h-3 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 12a9 9 0 1 1-6.219-8.56" strokeLinecap="round" /></svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3"><path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" /><circle cx="12" cy="13" r="3" /></svg>
              )}
            </span>
            <input type="file" accept="image/*" className="hidden" onChange={handlePhoto} disabled={photoUploading} />
          </label>
          <div className="flex-1 min-w-0">
            {editingName ? (
              <div>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveName();
                      if (e.key === 'Escape') {
                        setEditingName(false);
                        setName(currentMember?.name || '');
                      }
                    }}
                    className="flex-1 px-3 py-1.5 rounded-lg border border-hairline bg-surface font-body text-sm text-ink focus:outline-none focus:ring-2 focus:ring-lime focus:border-transparent"
                  />
                  <button
                    onClick={handleSaveName}
                    disabled={saving}
                    className="px-3 py-2 min-h-[44px] rounded-lg bg-lime text-canvas font-body text-sm font-medium hover:bg-lime transition-colors disabled:opacity-50"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => {
                      setEditingName(false);
                      setName(currentMember?.name || '');
                    }}
                    className="px-3 py-2 min-h-[44px] rounded-lg bg-surface text-ink-soft font-body text-sm font-medium hover:bg-surface transition-colors"
                  >
                    Cancel
                  </button>
                </div>
                {saveError && (
                  <p className="mt-1 font-body text-xs text-red-600">{saveError}</p>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span className="font-body font-semibold text-ink truncate">
                  {currentMember?.name}
                </span>
                <button
                  onClick={() => {
                    setName(currentMember?.name || '');
                    setEditingName(true);
                  }}
                  className="text-ink-muted hover:text-ink-soft transition-colors"
                  aria-label="Edit name"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                </button>
              </div>
            )}
            <div className="flex items-center gap-2 mt-1">
              <span
                className={`inline-block px-2 py-0.5 rounded-full text-xs font-body font-medium ${
                  currentMember?.type === 'adult'
                    ? 'bg-lime/10 text-lime'
                    : 'bg-mint/10 text-mint'
                }`}
              >
                {currentMember?.type === 'adult' ? 'Adult' : 'Kid'}
              </span>
              <span className="font-body text-xs text-ink-muted truncate">{user?.email}</span>
            </div>
            {photoError && <p className="mt-1 font-body text-xs text-red-600">{photoError}</p>}
          </div>
        </div>
      </div>

        {/* Household Card */}
        <div className="bg-panel rounded-2xl p-5 lg:p-6 border border-hairline">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg font-semibold text-ink">
            {membership?.household_name}
          </h3>
        </div>

        <div className="space-y-2">
          {members.map((member) => (
            <div key={member.id} className="flex items-center gap-3">
              <span className="w-7 h-7 rounded-full overflow-hidden inline-flex items-center justify-center text-xl shrink-0"><Avatar url={member.avatar_url} fallback={member.avatar} name={member.name} /></span>
              <span className="font-body text-sm text-ink truncate">{member.name}</span>
              {member.id === currentMember?.id && (
                <span className="font-body text-xs text-ink-muted">(You)</span>
              )}
            </div>
          ))}
        </div>

        <button
          onClick={() => navigate('/family')}
          className="mt-4 w-full flex items-center justify-center gap-1 py-2.5 rounded-xl bg-surface hover:bg-surface text-ink-soft font-body text-sm font-semibold transition-colors"
        >
          Manage Family
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
        </div>
      </div>

      {/* Second row - two column on desktop */}
      <div className="lg:grid lg:grid-cols-2 lg:gap-6 space-y-4 lg:space-y-0">
        {/* Meals you plan */}
        <div className="bg-panel rounded-2xl p-5 lg:p-6 border border-hairline">
          <h3 className="font-display text-lg font-semibold text-ink mb-1">Meals you plan</h3>
          <p className="font-body text-xs text-ink-muted mb-4">Choose which meals to include in your weekly plan and shopping list.</p>
          <div className="flex gap-2">
            {[
              { key: 'breakfast', label: 'Breakfast' },
              { key: 'lunch', label: 'Lunch' },
              { key: 'dinner', label: 'Dinner' },
            ].map(({ key, label }) => {
              const on = plannedMeals.includes(key);
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleToggleMeal(key)}
                  aria-pressed={on}
                  className={`flex-1 flex flex-col items-center gap-1.5 py-3 rounded-xl border transition-colors ${on ? 'bg-lime/15 border-lime/50' : 'bg-surface border-hairline'}`}
                >
                  <MealIcon meal={key} className={`w-5 h-5 ${on ? 'text-lime' : 'text-ink-muted'}`} />
                  <span className={`font-body text-sm font-medium ${on ? 'text-ink' : 'text-ink-muted'}`}>{label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Meal Schedule */}
        <div className="bg-panel rounded-2xl p-5 lg:p-6 border border-hairline">
        <h3 className="font-display text-lg font-semibold text-ink mb-1">Meal Schedule</h3>
        <p className="font-body text-xs text-ink-muted mb-4">Set your household's target meal times</p>
        <div className="space-y-3">
          {[
            { key: 'breakfast', label: 'Breakfast' },
            { key: 'lunch', label: 'Lunch' },
            { key: 'dinner', label: 'Dinner' },
          ].map(({ key, label }) => (
            <div key={key} className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <MealIcon meal={key} className="w-5 h-5 text-ink-soft" />
                <div>
                  <span className="font-body text-sm font-medium text-ink">{label}</span>
                  <p className="font-body text-[11px] text-ink-muted">{formatMealTime(mealTimes[key])}</p>
                </div>
              </div>
              <input
                type="time"
                defaultValue={mealTimes[key]}
                onChange={(e) => handleMealTimeChange(key, e.target.value)}
                className="px-2.5 py-1.5 rounded-lg border border-hairline bg-surface font-body text-sm text-ink focus:outline-none focus:ring-2 focus:ring-lime focus:border-transparent"
              />
            </div>
          ))}
        </div>
      </div>

        {/* Notifications */}
        {pushSupported && (
          <div className="bg-panel rounded-2xl p-5 lg:p-6 border border-hairline">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-display text-lg font-semibold text-ink">Notifications</h3>
              <p className="font-body text-xs text-ink-muted mt-0.5">
                {pushPermission === 'denied'
                  ? (isNative() ? 'Blocked in device settings' : 'Blocked in browser settings')
                  : 'Get notified about new plans and reminders'}
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={pushSubscribed}
              disabled={pushLoading || pushPermission === 'denied'}
              onClick={handleTogglePush}
              className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-lime focus:ring-offset-2 disabled:opacity-50 ${
                pushSubscribed ? 'bg-mint' : 'bg-surface'
              }`}
            >
              <span
                className={`inline-block h-5 w-5 rounded-full bg-panel shadow-sm transition-transform duration-200 ${
                  pushSubscribed ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          </div>
        )}
        {/* Placeholder if no push support to keep grid alignment */}
        {!pushSupported && <div className="hidden lg:block" />}
      </div>

      {/* Account - full width */}
      <div className="lg:max-w-md">
        <div className="bg-panel rounded-2xl p-5 lg:p-6 border border-hairline">
        <h3 className="font-display text-lg font-semibold text-ink mb-3">Account</h3>

        {showUpgrade && (
          <>
            <button
              type="button"
              onClick={() => navigate('/upgrade')}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-lime text-canvas font-body text-sm font-bold hover:bg-lime transition-colors"
            >
              🏆 Upgrade to Premium
            </button>
            <div className="my-3 border-t border-hairline" />
          </>
        )}

        {canManageSubscription && (
          <>
            <button
              type="button"
              onClick={handleManageSubscription}
              disabled={openingPortal}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-surface hover:bg-surface text-ink-soft font-body text-sm font-semibold transition-colors disabled:opacity-50"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Z" />
              </svg>
              {openingPortal ? 'Opening...' : 'Manage Subscription'}
            </button>
            <div className="my-3 border-t border-hairline" />
          </>
        )}

        {showPasswordForm ? (
          <div className="mb-3">
            <input
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="New password (min 8 characters)"
              minLength={8}
              className="w-full px-3 py-2.5 rounded-xl border border-hairline bg-surface font-body text-sm text-ink focus:outline-none focus:ring-2 focus:ring-lime focus:border-transparent"
            />
            {pwError && <p className="mt-1 font-body text-xs text-red-600">{pwError}</p>}
            <div className="flex gap-2 mt-2">
              <button
                type="button"
                onClick={handleChangePassword}
                disabled={pwSaving}
                className="flex-1 py-2.5 rounded-xl bg-lime text-canvas font-body text-sm font-semibold hover:bg-lime transition-colors disabled:opacity-50"
              >
                {pwSaving ? 'Saving…' : 'Save Password'}
              </button>
              <button
                type="button"
                onClick={() => { setShowPasswordForm(false); setNewPassword(''); setPwError(null); }}
                className="flex-1 py-2.5 rounded-xl bg-surface text-ink-soft font-body text-sm font-semibold hover:bg-surface transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => { setShowPasswordForm(true); setPwDone(false); }}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-surface hover:bg-surface text-ink-soft font-body text-sm font-semibold transition-colors mb-3"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
            </svg>
            {pwDone ? 'Password Updated ✓' : 'Change Password'}
          </button>
        )}

        <button
          type="button"
          onClick={handleExport}
          disabled={exporting}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-surface hover:bg-surface text-ink-soft font-body text-sm font-semibold transition-colors disabled:opacity-50"
        >
          {exporting ? (
            <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
            </svg>
          )}
          {exporting ? 'Exporting...' : 'Export My Data'}
        </button>

        <div className="my-3 border-t border-hairline" />

        <button
          type="button"
          onClick={() => setShowDeleteModal(true)}
          className="w-full py-2.5 rounded-xl text-red-600 font-body text-sm font-semibold hover:bg-red-50 transition-colors"
        >
          Delete Account
        </button>
        </div>

        {/* Sign Out */}
        <button
          onClick={signOut}
          className="w-full py-3 rounded-2xl border-2 border-red-200 text-red-600 font-body text-sm font-semibold hover:bg-red-50 active:bg-red-100 transition-colors mt-4"
        >
          Sign Out
        </button>
      </div>

      {showDeleteModal && (
        <DeleteAccountModal
          isLastMember={isLastMember}
          onConfirm={handleDeleteAccount}
          onClose={() => setShowDeleteModal(false)}
        />
      )}
    </div>
  );
}
